import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Eye, CreditCard, Building2 } from "lucide-react";
import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface PaymentDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  paymentType: "credit" | "bank";
  lastFour: string | null;
  bankLastFour: string | null;
  cardExpiry?: string | null;
  cardCvv?: string | null;
  fullCardNumber?: string | null;
}

export const PaymentDetailsDialog = ({
  isOpen,
  onClose,
  paymentType,
  lastFour,
  bankLastFour,
  cardExpiry,
  cardCvv,
  fullCardNumber,
}: PaymentDetailsDialogProps) => {
  const { canDelete, isLoading } = useAdminPermissions();

  console.log("PaymentDetailsDialog rendered with:", {
    paymentType,
    lastFour,
    fullCardNumber,
    canDelete
  });

  if (isLoading) {
    return null;
  }

  const formatCardNumber = (number: string) => {
    console.log("Formatting card number:", number);
    if (!number) return '';
    const cleaned = number.replace(/\D/g, '');
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ') : number;
  };

  const displayCardNumber = () => {
    if (canDelete && fullCardNumber) {
      console.log("Displaying full card number for admin");
      return formatCardNumber(fullCardNumber);
    }
    console.log("Displaying masked card number");
    return lastFour ? `•••• •••• •••• ${lastFour}` : '•••• •••• •••• ••••';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {paymentType === "credit" ? (
              <>
                <CreditCard className="h-5 w-5" />
                Credit Card Details
              </>
            ) : (
              <>
                <Building2 className="h-5 w-5" />
                Bank Account Details
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            View sensitive payment information. Only authorized personnel can see full details.
          </DialogDescription>
        </DialogHeader>
        
        {!canDelete ? (
          <Alert variant="destructive">
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You don't have permission to view sensitive payment details.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="p-6">
            <div className="space-y-4">
              {paymentType === "credit" ? (
                <div className="space-y-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-2">Card Number</p>
                    <p className="text-lg font-medium">
                      {displayCardNumber()}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-2">Expiration Date</p>
                    <p className="text-lg font-medium">{cardExpiry || "Not available"}</p>
                  </div>
                  {canDelete && (
                    <div className="text-center">
                      <p className="text-sm text-gray-500 mb-2">CVV</p>
                      <p className="text-lg font-medium">{cardCvv || "Not available"}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-2">Account Number</p>
                  <p className="text-lg font-medium">•••• •••• •••• {bankLastFour}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};